import React, { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import {
  Badge,
  Box,
  Button,
  Divider,
  HStack,
  SimpleGrid,
  Text,
  VStack
} from '@chakra-ui/react';
import PropTypes from 'prop-types';

import FormInput from '../../../../../components/form/FormInput';
import { hideModal } from '../../../../../redux/modules/modal';
import { useUpdateCompanyRole } from '../../../../../services/company-service';
import colors from '../../../../../styles/theme/foundation/colors';
import { unique } from '../../../../../utils/arrayUtil';
import { groupedPermissions } from '../permissions';

/**
 * Converts a permission name like "create_users" → "Create Users"
 * Used as a fallback when display_name is not provided.
 */
const humanizeName = name => name
  .split('_')
  .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
  .join(' ');

/**
 * Build a lookup map from permission name → { labelName, toolTip } using the
 * API-driven permissionGroups structure passed in from the parent.
 */
const buildPermissionDisplayMap = permissionGroups => {
  const map = {};

  permissionGroups.forEach(group => {
    group.permissions.forEach(perm => {
      map[perm.inputName] = {
        labelName: perm.labelName,
        toolTip: perm.toolTip
      };
    });
  });

  return map;
};

const RoleViewEditModal = ({
  role,
  companyId,
  isEditing: initialIsEditing,
  companyPermissionGroups,
  departmentPermissionGroups,
  onEditStart
}) => {
  const dispatch = useDispatch();
  const [isEditing, setIsEditing] = React.useState(initialIsEditing || false);

  const { mutate: updateRole, isLoading: isUpdating } = useUpdateCompanyRole({ companyId });

  const allPermissionGroups = [...companyPermissionGroups, ...departmentPermissionGroups];
  const permDisplayMap = buildPermissionDisplayMap(allPermissionGroups);

  // Collect the role's raw permission names
  const rolePermissionNames = (role.permissions || []).map(p => (typeof p === 'string' ? p : p.name));

  // Build a set for quick look-up (expand any grouped keys back to their expanded names for display)
  const expandedRolePermissions = new Set(rolePermissionNames);

  // Form defaults reflecting the role's current state.
  // For switches: check any permission that is in the role's permission list.
  // For grouped permissions: check the parent key when ALL expanded children are present.
  const editDefaults = useMemo(() => {
    const defaults = { name: role.displayName || role.name, description: role.description || '' };

    allPermissionGroups.forEach(group => {
      group.permissions.forEach(perm => {
        const expandedChildren = groupedPermissions[perm.inputName];

        if (perm.alwaysGranted) {
          // Included in every role — rendered as a permanently-on, disabled switch
          defaults[perm.inputName] = true;
        } else if (expandedChildren) {
          defaults[perm.inputName] = expandedChildren.every(child => expandedRolePermissions.has(child));
        } else {
          defaults[perm.inputName] = expandedRolePermissions.has(perm.inputName);
        }
      });
    });

    return defaults;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, companyPermissionGroups, departmentPermissionGroups]);

  // Defaults must be in react-hook-form BEFORE the edit-mode switches mount:
  // Chakra's Switch only syncs its visual state from the underlying input at
  // mount, so a reset() fired after mounting leaves the switches visually off.
  const useFormMethods = useForm({ defaultValues: editDefaults });
  const { handleSubmit, reset } = useFormMethods;

  const handleEditClick = () => {
    reset(editDefaults);
    setIsEditing(true);

    if (onEditStart) onEditStart();
  };

  const onSubmit = formData => {
    const { name, description } = formData;

    const dataWithoutMeta = { ...formData };

    delete dataWithoutMeta.name;
    delete dataWithoutMeta.description;

    // Collect plain permissions checked directly
    const selectedPermissions = Object.keys(dataWithoutMeta).filter(perm => dataWithoutMeta[perm]);

    // Expand grouped permissions into their real permission names
    const selectedGroupedPermissions = Object.keys(groupedPermissions).reduce((acc, perm) => {
      if (dataWithoutMeta[perm]) {
        return [...acc, ...groupedPermissions[perm]];
      }

      return acc;
    }, []);

    const allPermissions = unique([...selectedPermissions, ...selectedGroupedPermissions]);

    updateRole(
      { roleId: role.id, name, description, permissions: allPermissions },
      { onSuccess: () => dispatch(hideModal()) }
    );
  };

  if (isEditing) {
    return (
      <Box>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box mb={6}>
            <FormInput
              required
              inputName="name"
              labelName="Role Name"
              placeholder="Department Auditor"
              useFormMethods={useFormMethods}
            />
            <FormInput
              inputName="description"
              labelName="Description"
              placeholder="This role is for our auditors in zone A"
              type="textAreaSm"
              useFormMethods={useFormMethods}
            />
          </Box>

          <Divider mb={4} />

          <Text mb={2} textAlign="left" textStyle="bodyMd500">Company Tier Permissions</Text>
          {companyPermissionGroups.map(group => (
            <Box borderBottom="1px solid" borderBottomColor="greyScale.200" key={group.groupId} mb={2} pb={4} pt={2}>
              <Text mb={4} textAlign="left" textDecoration="underline" textStyle="bodySm500">
                {group.groupLabel}
              </Text>
              <SimpleGrid columns={2} spacing={1}>
                {group.permissions.map(perm => (
                  <FormInput
                    inputName={perm.inputName}
                    isDisabled={perm.alwaysGranted}
                    justifyContent="space-between"
                    key={perm.inputName}
                    labelName={perm.labelName}
                    pr={4}
                    textAlign="left"
                    toolTip={perm.toolTip}
                    type="switch"
                    useFormMethods={useFormMethods}
                  />
                ))}
              </SimpleGrid>
            </Box>
          ))}

          <Text mb={2} mt={4} textAlign="left" textStyle="bodyMd500">Department Tier Permissions</Text>
          {departmentPermissionGroups.map(group => (
            <Box borderBottom="1px solid" borderBottomColor="greyScale.200" key={group.groupId} mb={2} pb={4} pt={2}>
              <Text mb={4} textAlign="left" textDecoration="underline" textStyle="bodySm500">
                {group.groupLabel}
              </Text>
              <SimpleGrid columns={2} spacing={1}>
                {group.permissions.map(perm => (
                  <FormInput
                    inputName={perm.inputName}
                    isDisabled={perm.alwaysGranted}
                    justifyContent="space-between"
                    key={perm.inputName}
                    labelName={perm.labelName}
                    pr={4}
                    textAlign="left"
                    toolTip={perm.toolTip}
                    type="switch"
                    useFormMethods={useFormMethods}
                  />
                ))}
              </SimpleGrid>
            </Box>
          ))}

          <HStack justify="flex-end" mt={6}>
            <Button
              isLoading={isUpdating}
              type="submit"
              variant="primary"
            >
              Save Changes
            </Button>
            <Button
              variant="secondary"
              onClick={() => setIsEditing(false)}
            >
              Cancel
            </Button>
          </HStack>
        </form>
      </Box>
    );
  }

  // --- View mode ---
  return (
    <Box>
      {role.description && (
        <Text color="greyScale.500" mb={4} textStyle="bodySm">
          {role.description}
        </Text>
      )}

      {rolePermissionNames.length === 0 ? (
        <Text color="greyScale.400" textStyle="bodySm">No permissions assigned.</Text>
      ) : (
        <VStack align="stretch" spacing={4}>
          {allPermissionGroups
            .filter(group => group.permissions.some(
              perm => expandedRolePermissions.has(perm.inputName)
                || (groupedPermissions[perm.inputName]
                    && groupedPermissions[perm.inputName].some(child => expandedRolePermissions.has(child)))
            ))
            .map(group => (
              <Box key={group.groupId}>
                <Text mb={2} textAlign="left" textDecoration="underline" textStyle="bodySm500">
                  {group.groupLabel}
                </Text>
                <HStack flexWrap="wrap" spacing={2}>
                  {group.permissions
                    .filter(perm => {
                      const expanded = groupedPermissions[perm.inputName];

                      if (expanded) {
                        return expanded.some(child => expandedRolePermissions.has(child));
                      }

                      return expandedRolePermissions.has(perm.inputName);
                    })
                    .map(perm => (
                      <Badge
                        bgColor={colors.primaryScale['100']}
                        fontWeight="500"
                        key={perm.inputName}
                        mb={1}
                        p={1}
                        variant="subtle"
                      >
                        {permDisplayMap[perm.inputName]?.labelName || humanizeName(perm.inputName)}
                      </Badge>
                    ))}
                </HStack>
              </Box>
            ))}
        </VStack>
      )}

      <HStack justify="flex-end" mt={6}>
        <Button variant="primary" onClick={handleEditClick}>
          Edit Role
        </Button>
        <Button variant="secondary" onClick={() => dispatch(hideModal())}>
          Close
        </Button>
      </HStack>
    </Box>
  );
};

RoleViewEditModal.propTypes = {
  role: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string,
    displayName: PropTypes.string,
    description: PropTypes.string,
    permissions: PropTypes.array
  }).isRequired,
  companyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  isEditing: PropTypes.bool,
  companyPermissionGroups: PropTypes.array.isRequired,
  departmentPermissionGroups: PropTypes.array.isRequired,
  onEditStart: PropTypes.func
};

export default RoleViewEditModal;
